import cn from 'classnames'

import { getQualityBandColor } from '../../helpers/getQualityBandColor'

import styles from './QualityGauge.module.scss'

const CENTER_X = 50
const CENTER_Y = 48
const RADIUS = 38
const ARC_WIDTH = 12
const NEEDLE_LENGTH = 30
const EMPTY_COLOR = 'var(--color-gray-200)'

/* The half circle split at the band limits of getQualityBand, with a small gap between the bands. */
const ZONES = [
  { from: 0, to: 49, color: getQualityBandColor('Low') },
  { from: 51, to: 79, color: getQualityBandColor('Moderate') },
  { from: 81, to: 100, color: getQualityBandColor('High') },
]

const toPoint = (score: number, radius: number) => {
  const angle = Math.PI * (1 - score / 100)

  return [CENTER_X + radius * Math.cos(angle), CENTER_Y - radius * Math.sin(angle)]
}

const getArcPath = (from: number, to: number) => {
  const [x1, y1] = toPoint(from, RADIUS)
  const [x2, y2] = toPoint(to, RADIUS)

  return `M${x1} ${y1} A${RADIUS} ${RADIUS} 0 0 1 ${x2} ${y2}`
}

interface IQualityGaugeProps {
  /** `null` draws the scale grey and without a needle. */
  score: number | null
  height?: number
  /** The needle swings up from 0 when the gauge appears. */
  animated?: boolean
  /** A white needle, for the gauge on a dark ground such as a tooltip. */
  inverted?: boolean
}

export const QualityGauge = ({
  score,
  height = 16,
  animated = false,
  inverted = false,
}: IQualityGaugeProps) => {
  const hasScore = score !== null && Number.isFinite(score)
  /* The needle is drawn at 0 and turned to the score, so the swing is a plain rotation. */
  const angle = (hasScore ? Math.min(100, Math.max(0, score)) : 0) * 1.8

  return (
    <svg
      className={cn(styles.root, { [styles.inverted]: inverted, [styles.animated]: animated })}
      width={(height * 100) / 56}
      height={height}
      viewBox="0 0 100 56"
      aria-hidden="true"
    >
      {ZONES.map(({ from, to, color }) => (
        <path
          key={from}
          d={getArcPath(from, to)}
          stroke={hasScore ? color : EMPTY_COLOR}
          strokeWidth={ARC_WIDTH}
          fill="none"
        />
      ))}
      {hasScore && (
        <>
          <path
            className={styles.needle}
            d={`M${CENTER_X} ${CENTER_Y} L${CENTER_X - NEEDLE_LENGTH} ${CENTER_Y}`}
            strokeWidth={3.5}
            strokeLinecap="round"
            style={{ transform: `rotate(${angle}deg)` }}
          />
          <circle className={styles.hub} cx={CENTER_X} cy={CENTER_Y} r={5} />
        </>
      )}
    </svg>
  )
}

QualityGauge.displayName = 'QualityGauge'
